import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { view } from "@risingstack/react-easy-state";

import isNil from "lodash-es/isNil";

import { useEffect, useRef, useState } from "react";

import { productImageUrl } from "../../common/imageUtils";
import { queueImpression } from "../../common/tracking";
import { PageType, type Product } from "../../common/types/productTypes";
import { LIST_TYPE } from "../../common/types/trackingTypes";

import PriceBomb from "../../components/common/PriceBomb";

import promotionStore from "../../stores/promotionStore";
import searchStore from "../../stores/search/searchStore";

import Badges from "../../components/Badges";
import Favorite from "../../components/Favorite";
import Labels from "../../components/Labels";

import theme from "../../themes/theme";

const listName = "Produktdetaljside";

type ProductDetailsImageProps = {
   product: Product;
};

const productSearchDomain = theme.searchConfig.searchDomains.find((searchDomain) => searchDomain.type === "product");

const algoliaIndex = productSearchDomain?.indexName;

const ProductDetailsImage = ({ product }: ProductDetailsImageProps) => {
   const imageRef = useRef<HTMLImageElement>(null);
   const [imgLoading, setImageLoading] = useState(true);
   const [showSpinner, setShowSpinner] = useState(false);
   const spinnerDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const listInformation = {
      type: LIST_TYPE.CATEGORY,
      name: listName,
      length: 1
   };

   useEffect(() => {
      setImageLoading(true);
      setShowSpinner(false);

      spinnerDelayRef.current = setTimeout(() => {
         setShowSpinner(true);
      }, 300);

      if (!isNil(product)) {
         queueImpression(product, listName, 1);
         if (algoliaIndex) {
            searchStore.reportDetailsViewed(product.sku, algoliaIndex);
         }
      }

      return () => {
         if (spinnerDelayRef.current) {
            clearTimeout(spinnerDelayRef.current);
         }
      };
   }, [product]);

   const handleImageLoad = () => {
      setImageLoading(false);
      setShowSpinner(false);
      if (spinnerDelayRef.current) {
         clearTimeout(spinnerDelayRef.current);
      }
   };

   return (
      <>
         <div className="tw-h-full tw-w-full tw-items-center tw-justify-center">
            <div className="tw-absolute tw-right-0 tw-flex tw-flex-col tw-items-center">
               {!product.expired && <Favorite sku={product.sku} listInformation={listInformation} position={1} />}
               <Badges badges={product.badges} page={PageType.ProductDetailsPage} />
            </div>

            {showSpinner && imgLoading && (
               <div className="tw-flex tw-size-full tw-items-center tw-justify-center">
                  <ArrowPathIcon className="tw-animate-spin tw-h-8" />
               </div>
            )}

            <div className={`tw-flex tw-w-full tw-items-center tw-justify-center ${imgLoading ? "tw-hidden" : ""}`}>
               <img
                  ref={imageRef}
                  alt={`Produktbilde av ${product.name}`}
                  src={productImageUrl(product.sku, 380)}
                  onLoad={handleImageLoad}
                  className="tw-mx-auto tw-block tw-w-full tw-max-w-40 md:tw-max-w-60"
               />
            </div>
         </div>

         <Labels labels={product.labels} page={PageType.ProductDetailsPage} />

         {promotionStore.getPromotionsBySku(product.sku).map((promotion) => (
            <PriceBomb size="large" key={promotion.banner}>
               {promotion.banner}
            </PriceBomb>
         ))}
      </>
   );
};

export default view(ProductDetailsImage);
